import React, { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@mui/material';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useSort from 'hooks/useSort';

import { getDocumentHistoryAction } from 'store/documents/actions';
import { getDocumentHistory } from 'store/documents/selectors';

import { DocumentTestIds } from '../../document.testIds';

import { historyTableConfig } from './HistoryTable.config';
import { EVENT_DATE } from './HistoryTable.constants';

const containerStyles = {
  maxHeight: 600,
  width: 600,
};

const defaultOrder = {
  direction: 'desc',
  id: EVENT_DATE,
};

const HistoryTable = ({ docId, setIsLoading }) => {
  const dispatch = useDispatch();

  const documentHistory = useSelector(getDocumentHistory);

  const [data, setData] = useState([]);
  const { order, onRequestSort } = useSort(defaultOrder);

  const sortData = useMemo(
    () =>
      _.orderBy(
        data,
        [
          action => {
            switch (order.id) {
              case EVENT_DATE:
                return new Date(action.eventDate);

              default:
                return action[order.id]?.toLowerCase();
            }
          },
        ],
        [order.direction],
      ).map(action => ({ ...action, id: action.actionId })),
    [data, order],
  );

  useEffectSafeStrictMode(() => {
    setIsLoading(true);
    dispatch(getDocumentHistoryAction({ documentId: docId }))
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, docId, setIsLoading]);

  useEffect(() => {
    const historyList = Object.values(documentHistory).map(action => ({
      actionId: _.uniqueId(),
      ...action,
    }));

    setData(historyList);
  }, [documentHistory]);

  return (
    <Box sx={containerStyles}>
      <Box data-testid={DocumentTestIds.ACTIONS_TABLE}>
        <TableCDS
          isStickyHeader
          config={historyTableConfig}
          data={sortData}
          order={order}
          onRequestSort={onRequestSort}
        />
      </Box>
    </Box>
  );
};

HistoryTable.propTypes = {
  docId: PropTypes.string.isRequired,
  ...WithLoadingPropTypes,
};

export default withLoading(memo(HistoryTable));
